import {
  GuidanceTooltipsButton,
  type GuidanceTooltipsButtonRef,
} from '@carboncopyco/editor-sdk';
import type { IconProps } from '@carboncopyco/icons';
import { type IGlobalTab, TabType } from '@carboncopyco/types/desktop';
import { Button } from '@carboncopyco/ui';
import { AnalyticsTrackEventName, useAnalytics } from '@carboncopyco/utils';
import { type ReactNode, useRef } from 'react';
import { isOverlayTab } from '../../../shared/globalTabUtil';
import { useFlagsFallback } from '../../hooks/useFlagsFallback';
import { useNavigation } from '../../hooks/useNavigation';
import { useTabManager } from '../../services/TabManager';
import { isActiveOrAllInnerTabsLoaded } from '../../shared/utils';
import { useUser } from '../../store/context';
import {
  BrowserNavbar,
  BrowserNavbarInput,
  BrowserNavbarNavigation,
} from '../BrowserNavbar';
import type { IEditorPageInfo } from '../EditorContainer/hooks/useEditorPageInfo';
import { PersonaHeader } from '../Persona/PersonaHeader';
import SpeakerPanelButton from '../SpeakerPanelButton/SpeakerPanelButton';
import { TextExtraction } from '../TextExtraction';
import useGetPageUrl from './useGetPageUrl';

type Props = {
  tab: IGlobalTab;
  speakerPannel: {
    isVisible: boolean;
    isDisabled?: boolean;
  };
  additionalButtons?: ReactNode;
  ctaButton?: {
    title: string;
    onClick: () => void;
    idDisabled?: boolean;
    icon: React.ReactElement<IconProps>;
  };
  urlEditable?: boolean;
  hideNavbar?: boolean;
  pageInfo?: IEditorPageInfo;
  onGoBack?: () => void;
  onGoForward?: () => void;
  onRestart?: () => void;
  onSubmitUrl?: (url: string) => void;
};

const PresentAndPreviewToolbar = ({
  tab,
  additionalButtons,
  speakerPannel,
  ctaButton,
  urlEditable = false,
  hideNavbar,
  pageInfo,
  onGoBack,
  onGoForward,
  onRestart,
  onSubmitUrl,
}: Props) => {
  const flags = useFlagsFallback();
  const { trackEvent } = useAnalytics();
  const guidanceTooltipsButtonRef = useRef<GuidanceTooltipsButtonRef>(null);

  const { canGoBack, canGoForward, goBack, goForward, isLoading } =
    useNavigation(tab);

  const { personas } = useUser();
  const { switchGlobalTabSession } = useTabManager();

  const pageUrl = useGetPageUrl({ tab, pageInfo });

  const handleSwitchActivePersona = (personaId: string) => {
    switchGlobalTabSession(tab, personaId);
    guidanceTooltipsButtonRef.current?.reset();
  };

  const handleGuidanceTooltipsButtonClick = (checkedItem: boolean) => {
    trackEvent(AnalyticsTrackEventName.DEMO_TOGGLE_GUIDANCE_TOOLTIPS, {
      checkedItem,
    });
  };

  return (
    <BrowserNavbar shouldAddRightPadding={!ctaButton}>
      <div className="flex items-center flex-grow min-w-0 space-x-5 w-full">
        <BrowserNavbarNavigation
          onBack={onGoBack || goBack}
          onForward={onGoForward || goForward}
          isBackDisabled={!onGoBack && !canGoBack}
          isForwardDisabled={!onGoForward && !canGoForward}
          onReloadNavigation={
            onRestart ||
            (flags.refreshPageButton
              ? () => window.dsDesktop.navigation.reload()
              : () => {})
          }
          onStopReloadNavigation={
            flags.refreshPageButton
              ? () => window.dsDesktop.navigation.stopReload()
              : () => {}
          }
          isLoadingNavigation={isLoading}
        />
        {!hideNavbar && (
          <BrowserNavbarInput
            url={pageUrl ?? ''}
            isPreviewPillVisible={!!ctaButton}
            onSubmitUrl={onSubmitUrl}
            urlEditable={urlEditable}
          />
        )}
      </div>
      {additionalButtons}
      <div className="flex gap-2.5 pl-4">
        {[
          TabType.PREVIEW_DEMO,
          TabType.PRESENT_DEMO,
          TabType.GROUP_DEMO_PRESENTATION,
          TabType.GROUP_DEMO_PREVIEW,
          TabType.PREVIEW_OVERLAY,
        ].includes(tab.type) && (
          <GuidanceTooltipsButton
            ref={guidanceTooltipsButtonRef}
            onButtonClick={handleGuidanceTooltipsButtonClick}
          />
        )}
        {speakerPannel.isVisible && (
          <SpeakerPanelButton
            tab={tab}
            isButtonDisabled={speakerPannel.isDisabled}
          />
        )}
        {personas?.defaultId && (
          <PersonaHeader
            personasData={personas}
            onSwitchActivePersona={handleSwitchActivePersona}
            isLoaded={isActiveOrAllInnerTabsLoaded(tab)}
            isReadOnly={
              !isOverlayTab(tab) && tab.type !== TabType.BROWSER_WINDOW
            }
          />
        )}
        {tab.type === TabType.PREVIEW_CLONE && <TextExtraction tab={tab} />}
      </div>
      {ctaButton && (
        <>
          <div className="w-[16px] h-[60px] bg-ds-primary-300">
            <div className="w-full h-full bg-ds-natural-600 rounded-br-full" />
          </div>
          <div className="p-1.5 pt-1.5 pb-3 mt-0.5 -mb-1 bg-ds-primary-300 flex flex-row relative rounded-tl-md">
            <Button
              onClick={ctaButton.onClick}
              label={ctaButton.title}
              disabled={ctaButton.idDisabled}
              leftIcon={ctaButton.icon}
            />
          </div>
        </>
      )}
    </BrowserNavbar>
  );
};

export default PresentAndPreviewToolbar;
